import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { TabBar } from '../components/TabBar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import StudentFormView from '../components/StudentFormView';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import { AttendancePanel } from '../components/AttendancePanel';
import { GradingStudentSelector } from '../components/GradingStudentSelector';
import {
    ArrowLeft, Users, UserPlus, UserMinus, GraduationCap, Check, X, Save, Award,
    ChevronLeft, ChevronRight, ClipboardCheck, UsersRound, Trash2, Loader2,
    Calendar, FileText, Upload, Download, AlertCircle, PenTool, FileDown,
    Lock, Eye, Search
} from 'lucide-react';

const AssessmentDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { getAuthHeaders, API, user: currentUser } = useAuth();

    const [assessment, setAssessment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('students');

    // Students
    const [enrolledStudents, setEnrolledStudents] = useState([]);
    const [allStudents, setAllStudents] = useState([]);
    const [enrollModalOpen, setEnrollModalOpen] = useState(false);
    const [enrollSearch, setEnrollSearch] = useState('');

    // Groups
    const [groups, setGroups] = useState([]);
    const [groupModalOpen, setGroupModalOpen] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [uploadingGroups, setUploadingGroups] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);

    // Attendance
    const [predefinedComments, setPredefinedComments] = useState([]);
    const [panelAttendance, setPanelAttendance] = useState({});
    const [attComments, setAttComments] = useState({});
    const [attPredefined, setAttPredefined] = useState({});
    const [currentAttIdx, setCurrentAttIdx] = useState(0);
    const [selectedGroupFilter, setSelectedGroupFilter] = useState('all');
    const [attSaving, setAttSaving] = useState(false);
    const [attSearch, setAttSearch] = useState('');

    // Navigate to student in specific tab
    const goToStudentTab = (studentId, tab) => {
        const idx = filteredStudents.findIndex(s => s.id === studentId);
        if (tab === 'attendance') {
            setActiveTab('attendance');
            if (idx >= 0) setCurrentAttIdx(idx);
        } else if (tab === 'assessment') {
            setActiveTab('assessment');
            const gradeIdx = enrolledStudents.findIndex(s => s.id === studentId);
            if (gradeIdx >= 0) setCurrentGradeIdx(gradeIdx);
        }
    };
    // Assessment grading
    const [formData, setFormData] = useState(null);
    const [panelGrades, setPanelGrades] = useState({});
    const [currentGradeIdx, setCurrentGradeIdx] = useState(0);
    const [scores, setScores] = useState({});
    const [feedback, setFeedback] = useState('');
    const [gradeSaving, setGradeSaving] = useState(false);

    // Per-section comments (fetched from form template)
    const [sectionComments, setSectionComments] = useState([]);
    const [sectionFreeText, setSectionFreeText] = useState({});
    const [sectionPills, setSectionPills] = useState({});

    // Assessor locking
    const [locks, setLocks] = useState([]);
    const lockPollRef = useRef(null);
    const heartbeatRef = useRef(null);
    const [currentLockOwn, setCurrentLockOwn] = useState(false);
    const [expandedStudentId, setExpandedStudentId] = useState(null);
    const [isDirty, setIsDirty] = useState(false);

    // Signatures
    const [signatures, setSignatures] = useState({});
    const [sigModalOpen, setSigModalOpen] = useState(null);
    const [sigType, setSigType] = useState('drawn');
    const [typedSig, setTypedSig] = useState('');
    const sigCanvasRef = useRef(null);

    // View student form modal
    const [viewFormOpen, setViewFormOpen] = useState(false);
    const [viewFormStudent, setViewFormStudent] = useState(null);

    const fetchAssessment = useCallback(async () => {
        try {
            const [aRes, sRes, allRes, gRes, attRes, cRes, grRes] = await Promise.all([
                axios.get(`${API}/assessments/${id}`, { headers: getAuthHeaders() }),
                axios.get(`${API}/assessments/${id}/enrolled-students`, { headers: getAuthHeaders() }),
                axios.get(`${API}/students`, { headers: getAuthHeaders() }),
                axios.get(`${API}/assessments/${id}/groups`, { headers: getAuthHeaders() }),
                axios.get(`${API}/attendance/assessment/${id}`, { headers: getAuthHeaders() }),
                axios.get(`${API}/admin/attendance-comments`, { headers: getAuthHeaders() }),
                axios.get(`${API}/assessment-grades/${id}`, { headers: getAuthHeaders() })
            ]);
            const a = aRes.data;
            setAssessment(a);
            setEnrolledStudents(sRes.data);
            setAllStudents(allRes.data);
            setGroups(gRes.data);
            setPredefinedComments(cRes.data);

            const attMap = {}, commentMap = {}, predMap = {};
            sRes.data.forEach(s => { attMap[s.id] = false; commentMap[s.id] = ''; predMap[s.id] = []; });
            attRes.data.forEach(r => { attMap[r.student_id] = r.present; commentMap[r.student_id] = r.comment || ''; predMap[r.student_id] = r.predefined_comments || []; });
            setPanelAttendance(attMap);
            setAttComments(commentMap);
            setAttPredefined(predMap);

            const gMap = {};
            grRes.data.forEach(g => { gMap[g.student_id] = g; });
            setPanelGrades(gMap);

            if (a.assessment_form_id) {
                try {
                    const fRes = await axios.get(`${API}/admin/assessment-forms/${a.assessment_form_id}`, { headers: getAuthHeaders() });
                    setFormData(fRes.data.form_data);
                } catch (e) { setFormData(null); }
            }

            // Fetch criterion comments from the form template
            if (a.assessment_form_id) {
                try {
                    const scRes = await axios.get(`${API}/admin/form-comments/${a.assessment_form_id}`, { headers: getAuthHeaders() });
                    setSectionComments(scRes.data);
                } catch (e) { setSectionComments([]); }
            }

            // Fetch current locks
            try {
                const lRes = await axios.get(`${API}/assessments/${id}/locks`, { headers: getAuthHeaders() });
                setLocks(lRes.data);
            } catch (e) { setLocks([]); }

            if (sRes.data.length > 0) loadGrade(sRes.data[0].id, gMap);
        } catch (e) {
            toast.error('Failed to load assessment');
            navigate('/assessments');
        } finally { setLoading(false); }
    }, [id, API, getAuthHeaders, navigate]);

    useEffect(() => { fetchAssessment(); }, [fetchAssessment]);

    // Poll locks every 10 seconds when on assessment tab
    useEffect(() => {
        if (activeTab === 'assessment') {
            const poll = async () => {
                try {
                    const res = await axios.get(`${API}/assessments/${id}/locks`, { headers: getAuthHeaders() });
                    setLocks(res.data);
                } catch (e) { /* silent */ }
            };
            lockPollRef.current = setInterval(poll, 10000);
            return () => clearInterval(lockPollRef.current);
        } else {
            if (lockPollRef.current) clearInterval(lockPollRef.current);
        }
    }, [activeTab, id, API, getAuthHeaders]);

    // Heartbeat for own lock every 60 seconds
    useEffect(() => {
        if (activeTab === 'assessment' && currentLockOwn && enrolledStudents[currentGradeIdx]) {
            const sid = enrolledStudents[currentGradeIdx].id;
            const hb = async () => {
                try {
                    await axios.post(`${API}/assessments/${id}/heartbeat/${sid}`, {}, { headers: getAuthHeaders() });
                } catch (e) { /* silent */ }
            };
            heartbeatRef.current = setInterval(hb, 60000);
            return () => clearInterval(heartbeatRef.current);
        } else {
            if (heartbeatRef.current) clearInterval(heartbeatRef.current);
        }
    }, [activeTab, currentLockOwn, currentGradeIdx, enrolledStudents, id, API, getAuthHeaders]);

    // Cleanup lock on unmount
    useEffect(() => {
        return () => {
            if (lockPollRef.current) clearInterval(lockPollRef.current);
            if (heartbeatRef.current) clearInterval(heartbeatRef.current);
        };
    }, []);

    const acquireLock = async (studentId) => {
        try {
            const res = await axios.post(`${API}/assessments/${id}/lock/${studentId}`, {}, { headers: getAuthHeaders() });
            setCurrentLockOwn(res.data.own);
            // Refresh locks
            const lRes = await axios.get(`${API}/assessments/${id}/locks`, { headers: getAuthHeaders() });
            setLocks(lRes.data);
            return res.data;
        } catch (e) {
            return { locked: false, own: false };
        }
    };

    const releaseLock = async (studentId) => {
        try {
            await axios.post(`${API}/assessments/${id}/unlock/${studentId}`, {}, { headers: getAuthHeaders() });
            setCurrentLockOwn(false);
            const lRes = await axios.get(`${API}/assessments/${id}/locks`, { headers: getAuthHeaders() });
            setLocks(lRes.data);
        } catch (e) { /* silent */ }
    };

    const getLockForStudent = (studentId) => {
        return locks.find(l => l.student_id === studentId);
    };

    const isLockedByOther = (studentId) => {
        const lock = getLockForStudent(studentId);
        return lock && lock.locked_by !== currentUser?.id;
    };

    const loadGrade = (sid, gMap) => {
        const existing = (gMap || panelGrades)[sid];
        if (existing) {
            const sMap = {};
            existing.scores?.forEach(s => { sMap[s.criterion_id] = s.score; });
            setScores(sMap);
            setFeedback(existing.feedback || '');
            const ftMap = {}, pillMap = {};
            if (existing.section_comments) {
                Object.entries(existing.section_comments).forEach(([k, v]) => {
                    ftMap[k] = v.free_text || '';
                    pillMap[k] = v.pills || [];
                });
            }
            setSectionFreeText(ftMap);
            setSectionPills(pillMap);
        } else {
            setScores({});
            setFeedback('');
            setSectionFreeText({});
            setSectionPills({});
        }
        setIsDirty(false);
        loadSignatures(sid);
    };

    const loadSignatures = async (sid) => {
        try {
            const res = await axios.get(`${API}/assessment-signatures/${id}/${sid}`, { headers: getAuthHeaders() });
            const sigMap = {};
            res.data.forEach(s => { sigMap[s.role] = s; });
            setSignatures(sigMap);
        } catch (e) { setSignatures({}); }
    };

    const saveSignature = async (role) => {
        const student = enrolledStudents[currentGradeIdx];
        if (!student) return;
        let sigData;
        if (sigType === 'drawn') {
            if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) { toast.error('Please provide a signature'); return; }
            sigData = sigCanvasRef.current.toDataURL('image/png');
        } else {
            if (!typedSig.trim()) { toast.error('Please type your name'); return; }
            sigData = typedSig.trim();
        }
        try {
            const res = await axios.post(`${API}/assessment-signatures`, {
                assessment_id: id, student_id: student.id, role, signature: sigData, signature_type: sigType
            }, { headers: getAuthHeaders() });
            setSignatures(prev => ({ ...prev, [role]: res.data }));
            setSigModalOpen(null);
            setTypedSig('');
            toast.success(`${role.charAt(0).toUpperCase() + role.slice(1)} signature saved`);
        } catch (e) { toast.error('Failed'); }
    };

    const exportPDF = async () => {
        const student = enrolledStudents[currentGradeIdx];
        if (!student) return;
        try {
            const res = await axios.get(`${API}/reports/assessment/${id}/student/${student.id}/pdf`, {
                headers: getAuthHeaders(), responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `assessment_${student.student_id}.pdf`);
            document.body.appendChild(link); link.click(); link.remove();
            toast.success('PDF exported');
        } catch (e) { toast.error('Export failed'); }
    };

    // Enrollment
    const enrolledIds = enrolledStudents.map(s => s.id);
    const sortBySurname = (list) => [...list].sort((a, b) => {
        if ((a.year || 0) !== (b.year || 0)) return (a.year || 0) - (b.year || 0);
        const surnameA = (a.name || '').split(' ').pop().toLowerCase();
        const surnameB = (b.name || '').split(' ').pop().toLowerCase();
        return surnameA.localeCompare(surnameB);
    });
    const availableStudents = sortBySurname(allStudents.filter(s => s.year === assessment?.year && !enrolledIds.includes(s.id)));
    const filteredAvailable = enrollSearch
        ? availableStudents.filter(s => s.name.toLowerCase().includes(enrollSearch.toLowerCase()) || s.student_id.toLowerCase().includes(enrollSearch.toLowerCase()))
        : availableStudents;

    const handleEnroll = async (sid) => {
        try {
            await axios.post(`${API}/assessments/${id}/enroll/${sid}`, {}, { headers: getAuthHeaders() });
            const res = await axios.get(`${API}/assessments/${id}/enrolled-students`, { headers: getAuthHeaders() });
            setEnrolledStudents(res.data);
            setPanelAttendance(p => ({ ...p, [sid]: false }));
            setAttComments(p => ({ ...p, [sid]: '' }));
            setAttPredefined(p => ({ ...p, [sid]: [] }));
            toast.success('Student enrolled');
        } catch (e) { toast.error('Failed'); }
    };

    const handleUnenroll = async (sid) => {
        try {
            await axios.delete(`${API}/assessments/${id}/enroll/${sid}`, { headers: getAuthHeaders() });
            const res = await axios.get(`${API}/assessments/${id}/enrolled-students`, { headers: getAuthHeaders() });
            setEnrolledStudents(res.data);
            toast.success('Student removed');
        } catch (e) { toast.error('Failed'); }
    };

    const enrollAll = async () => {
        for (const s of availableStudents) {
            try { await axios.post(`${API}/assessments/${id}/enroll/${s.id}`, {}, { headers: getAuthHeaders() }); } catch (e) { /* skip */ }
        }
        const res = await axios.get(`${API}/assessments/${id}/enrolled-students`, { headers: getAuthHeaders() });
        setEnrolledStudents(res.data);
        res.data.forEach(s => {
            if (!panelAttendance.hasOwnProperty(s.id)) {
                setPanelAttendance(p => ({ ...p, [s.id]: false }));
                setAttComments(p => ({ ...p, [s.id]: '' }));
                setAttPredefined(p => ({ ...p, [s.id]: [] }));
            }
        });
        toast.success(`Enrolled all Year ${assessment.year} students`);
    };

    // Groups
    const createGroup = async () => {
        if (!newGroupName.trim()) return;
        try {
            await axios.post(`${API}/assessments/${id}/groups`, { name: newGroupName.trim(), student_ids: [] }, { headers: getAuthHeaders() });
            const res = await axios.get(`${API}/assessments/${id}/groups`, { headers: getAuthHeaders() });
            setGroups(res.data);
            setNewGroupName('');
            toast.success('Group created');
        } catch (e) { toast.error('Failed'); }
    };

    const toggleStudentInGroup = async (gid, sid) => {
        const g = groups.find(g => g.id === gid);
        if (!g) return;
        const ids = g.student_ids.includes(sid) ? g.student_ids.filter(i => i !== sid) : [...g.student_ids, sid];
        try {
            await axios.put(`${API}/assessments/${id}/groups/${gid}`, { student_ids: ids }, { headers: getAuthHeaders() });
            setGroups(prev => prev.map(g => g.id === gid ? { ...g, student_ids: ids } : g));
        } catch (e) { toast.error('Failed'); }
    };

    const deleteGroup = async (gid) => {
        try {
            await axios.delete(`${API}/assessments/${id}/groups/${gid}`, { headers: getAuthHeaders() });
            setGroups(prev => prev.filter(g => g.id !== gid));
            if (selectedGroupFilter === gid) setSelectedGroupFilter('all');
        } catch (e) { toast.error('Failed'); }
    };

    const handleGroupUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingGroups(true);
        setUploadResult(null);
        try {
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);
            const res = await axios.post(`${API}/assessments/${id}/groups/upload`, formDataUpload, {
                headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
            });
            setUploadResult(res.data);
            toast.success(res.data.message);
            const [sRes, gRes] = await Promise.all([
                axios.get(`${API}/assessments/${id}/enrolled-students`, { headers: getAuthHeaders() }),
                axios.get(`${API}/assessments/${id}/groups`, { headers: getAuthHeaders() })
            ]);
            setEnrolledStudents(sRes.data);
            setGroups(gRes.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Upload failed');
        } finally { setUploadingGroups(false); e.target.value = ''; }
    };

    const downloadTemplate = async () => {
        try {
            const res = await axios.get(`${API}/groups/upload-template`, { headers: getAuthHeaders(), responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a'); link.href = url;
            link.setAttribute('download', 'group_upload_template.xlsx');
            document.body.appendChild(link); link.click(); link.remove();
        } catch (e) { toast.error('Failed to download template'); }
    };

    // Attendance
    const filteredStudents = sortBySurname((selectedGroupFilter === 'all'
        ? enrolledStudents
        : enrolledStudents.filter(s => groups.find(g => g.id === selectedGroupFilter)?.student_ids.includes(s.id))
    ).filter(s => !attSearch || s.name.toLowerCase().includes(attSearch.toLowerCase()) || s.student_id.toLowerCase().includes(attSearch.toLowerCase())));

    const currentAttStudent = filteredStudents[currentAttIdx];
    const presentCount = Object.values(panelAttendance).filter(Boolean).length;

    const togglePredefined = (sid, label) => {
        setAttPredefined(prev => {
            const c = prev[sid] || [];
            return { ...prev, [sid]: c.includes(label) ? c.filter(l => l !== label) : [...c, label] };
        });
    };

    const saveAttendance = async () => {
        setAttSaving(true);
        try {
            const records = enrolledStudents.map(s => ({
                student_id: s.id, present: panelAttendance[s.id] || false,
                comment: attComments[s.id] || '', predefined_comments: attPredefined[s.id] || []
            }));
            await axios.post(`${API}/attendance`, { assessment_id: id, records }, { headers: getAuthHeaders() });
            toast.success('Attendance saved');
        } catch (e) { toast.error('Failed'); }
        finally { setAttSaving(false); }
    };

    // Assessment grading - filtered by group
    const gradeFilteredStudents = sortBySurname(selectedGroupFilter === 'all'
        ? enrolledStudents
        : enrolledStudents.filter(s => groups.find(g => g.id === selectedGroupFilter)?.student_ids?.includes(s.id)));
    const currentGradeStudent = enrolledStudents[currentGradeIdx];
    const currentGradeFilteredIdx = currentGradeStudent ? gradeFilteredStudents.findIndex(s => s.id === currentGradeStudent.id) : -1;
    const allCriteria = formData?.sections?.flatMap(s => s.criteria) || [];
    const totalScore = Object.values(scores).reduce((sum, s) => sum + s, 0);
    const maxScore = allCriteria.length * 8;
    const currentStudentLockedByOther = currentGradeStudent ? isLockedByOther(currentGradeStudent.id) : false;
    const currentStudentLock = currentGradeStudent ? getLockForStudent(currentGradeStudent.id) : null;

    const navigateGradeStudent = async (dir) => {
        const newFilteredIdx = currentGradeFilteredIdx + dir;
        if (newFilteredIdx >= 0 && newFilteredIdx < gradeFilteredStudents.length) {
            // Auto-save current student if dirty
            if (isDirty && currentGradeStudent) {
                await autoSaveCurrentGrade();
            }
            const newStudent = gradeFilteredStudents[newFilteredIdx];
            const newGlobalIdx = enrolledStudents.findIndex(s => s.id === newStudent.id);
            if (enrolledStudents[currentGradeIdx]) {
                await releaseLock(enrolledStudents[currentGradeIdx].id);
            }
            setCurrentGradeIdx(newGlobalIdx);
            loadGrade(newStudent.id, panelGrades);
            await acquireLock(newStudent.id);
        }
    };

    // When user first clicks into assessment tab, acquire lock on the first student
    useEffect(() => {
        if (activeTab === 'assessment' && enrolledStudents.length > 0 && formData) {
            acquireLock(enrolledStudents[currentGradeIdx]?.id);
        }
        return () => {
            // Release lock when leaving tab
            if (enrolledStudents[currentGradeIdx]) {
                releaseLock(enrolledStudents[currentGradeIdx].id).catch(() => {});
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const getScoreColor = (score, selected) => {
        if (selected) {
            const map = { 3: 'bg-red-500 text-white', 4: 'bg-orange-500 text-white', 5: 'bg-yellow-500 text-white', 6: 'bg-blue-500 text-white', 7: 'bg-green-500 text-white', 8: 'bg-emerald-600 text-white' };
            return map[score] || 'bg-gray-500 text-white';
        }
        const map = { 3: 'bg-red-50 text-red-700', 4: 'bg-orange-50 text-orange-700', 5: 'bg-yellow-50 text-yellow-700', 6: 'bg-blue-50 text-blue-700', 7: 'bg-green-50 text-green-700', 8: 'bg-emerald-50 text-emerald-700' };
        return map[score] || 'bg-gray-100 text-gray-600';
    };

    // Per-section pill comment helpers
    const toggleSectionPill = (sectionId, label) => {
        setSectionPills(prev => {
            const arr = prev[sectionId] || [];
            return { ...prev, [sectionId]: arr.includes(label) ? arr.filter(l => l !== label) : [...arr, label] };
        });
        setIsDirty(true);
    };

    const getCommentsForSection = (sectionId) => {
        return sectionComments.filter(c =>
            (c.section_id && c.section_id === sectionId) || !c.section_id
        );
    };

    const buildGradePayload = () => {
        const scoresList = allCriteria.map(c => ({ criterion_id: c.id, score: scores[c.id] !== undefined ? scores[c.id] : 0 }));
        const scObj = {};
        formData?.sections?.forEach(section => {
            scObj[section.id] = { free_text: sectionFreeText[section.id] || '', pills: sectionPills[section.id] || [] };
        });
        return { scoresList, scObj };
    };

    const autoSaveCurrentGrade = async () => {
        if (!currentGradeStudent) return;
        try {
            const { scoresList, scObj } = buildGradePayload();
            await axios.post(`${API}/assessment-grades`, {
                assessment_id: id, student_id: currentGradeStudent.id, scores: scoresList, feedback, section_comments: scObj
            }, { headers: getAuthHeaders() });
            const total = scoresList.reduce((s, x) => s + x.score, 0);
            setPanelGrades(prev => ({ ...prev, [currentGradeStudent.id]: { scores: scoresList, feedback, section_comments: scObj, total_score: total } }));
            setIsDirty(false);
        } catch (e) { console.error('Auto-save failed', e); }
    };

    const saveGrade = async () => {
        if (!currentGradeStudent) return;
        setGradeSaving(true);
        try {
            const { scoresList, scObj } = buildGradePayload();
            await axios.post(`${API}/assessment-grades`, {
                assessment_id: id, student_id: currentGradeStudent.id, scores: scoresList, feedback, section_comments: scObj
            }, { headers: getAuthHeaders() });
            const total = scoresList.reduce((s, x) => s + x.score, 0);
            setPanelGrades(prev => ({ ...prev, [currentGradeStudent.id]: { scores: scoresList, feedback, section_comments: scObj, total_score: total } }));
            setIsDirty(false);
            toast.success('Grade saved');
        } catch (e) { toast.error('Failed'); }
        finally { setGradeSaving(false); }
    };

    // Section comments upload
    const components = assessment?.components || ['attendance', 'assessment_form'];
    const tabs = [
        { id: 'students', label: 'Students', icon: Users },
        ...(components.includes('attendance') ? [{ id: 'attendance', label: 'Attendance', icon: ClipboardCheck }] : []),
        ...(components.includes('assessment_form') ? [{ id: 'assessment', label: 'Assessment', icon: Award }] : []),
    ];

    if (loading) return (
        <Layout>
            <div className="space-y-4">
                <div className="skeleton h-5 w-64 rounded"></div>
                <div className="skeleton h-8 w-96 rounded"></div>
                <div className="flex gap-3 mt-4">{[1,2,3].map(i => <div key={i} className="skeleton h-10 w-28 rounded-lg" />)}</div>
                <div className="skeleton h-64 rounded-xl mt-4"></div>
            </div>
        </Layout>
    );

    if (!assessment) return (
        <Layout><div className="text-center py-10"><p className="text-gray-500">Assessment not found</p><Button onClick={() => navigate('/assessments')} className="mt-4">Back to Assessments</Button></div></Layout>
    );

    return (
        <Layout>
            <div className="space-y-6 page-enter" data-testid="assessment-detail-page">
                {/* Breadcrumb */}
                <nav className="flex items-center gap-1.5 text-sm text-gray-400" data-testid="breadcrumb">
                    <button onClick={() => navigate('/assessments')} className="hover:text-ica-navy transition-colors">Assessments</button>
                    <span>/</span>
                    <span className="text-gray-700 font-medium truncate max-w-[200px]">{assessment.title}</span>
                </nav>
                {/* Header */}
                <div className="flex items-start gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/assessments')} className="mt-1 text-slate-500 hover:text-ica-navy dark:text-slate-400 dark:hover:text-ica-gold" data-testid="back-to-assessments" title="Back to Assessments">
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="flex-1">
                        <h1 className="font-heading text-3xl font-bold text-ica-navy">{assessment.title}</h1>
                        <div className="flex items-center gap-3 mt-1">
                            <span className="text-sm text-gray-500 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{assessment.date}</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-ica-navy/10 text-ica-navy font-medium">Year {assessment.year}</span>
                            <span className="text-sm text-gray-500 flex items-center gap-1"><Users className="w-3.5 h-3.5" />{enrolledStudents.length} students</span>
                            {components.map(c => (
                                <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-ica-gold/10 text-ica-gold-dark font-medium">
                                    {c === 'attendance' ? 'Attendance' : 'Assessment Form'}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <TabBar tabs={tabs} active={activeTab} onChange={setActiveTab} testIdPrefix="detail-tab" />

                {/* Students Tab */}
                {activeTab === 'students' && (
                    <div className="space-y-6" data-testid="students-tab">
                        <div className="flex items-center gap-3">
                            <Button onClick={() => setEnrollModalOpen(true)} data-testid="open-enroll-modal">
                                <UserPlus className="w-4 h-4 mr-2" /> Add Students
                            </Button>
                            {availableStudents.length > 0 && (
                                <Button variant="outline" onClick={enrollAll} data-testid="enroll-all-btn">
                                    <Users className="w-4 h-4 mr-2" /> Add All Year {assessment.year} ({availableStudents.length})
                                </Button>
                            )}
                            <Button variant="outline" onClick={() => setGroupModalOpen(true)} data-testid="manage-groups-btn">
                                <UsersRound className="w-4 h-4 mr-2" /> Manage Groups
                            </Button>
                        </div>

                        {groups.length > 0 && (
                            <div className="space-y-3">
                                <h3 className="text-sm font-semibold text-gray-700">Groups</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {groups.map(g => (
                                        <div key={g.id} onClick={() => setSelectedGroupFilter(prev => prev === g.id ? 'all' : g.id)}
                                            className={`bg-white rounded-xl border-2 p-4 cursor-pointer transition-all hover:shadow-md ${selectedGroupFilter === g.id ? 'border-ica-gold shadow-sm' : 'border-gray-100 hover:border-gray-200'}`}
                                            data-testid={`group-card-${g.id}`}>
                                            <div className="flex items-center justify-between mb-3">
                                                <p className="font-medium text-gray-900 text-sm">{g.name}</p>
                                                <span className="text-xs text-gray-400">{(g.student_ids || []).filter(sid => enrolledStudents.some(s => s.id === sid)).length} students</span>
                                            </div>
                                            <div className="flex gap-1 flex-wrap">
                                                {enrolledStudents.filter(s => g.student_ids.includes(s.id)).map(s => (
                                                    <span key={s.id} className="px-2 py-0.5 rounded-full text-xs bg-ica-gold/10 text-ica-gold-dark font-medium">{s.name.split(' ')[0]}</span>
                                                ))}
                                                {g.student_ids.length === 0 && <span className="text-xs text-gray-400">No students assigned</span>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-sm font-semibold text-gray-700">Enrolled Students ({enrolledStudents.length})</h3>
                                {groups.length > 0 && (
                                    <div className="flex gap-1.5 flex-wrap">
                                        <button onClick={() => setSelectedGroupFilter('all')}
                                            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${selectedGroupFilter === 'all' ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                            data-testid="students-group-filter-all">
                                            All ({enrolledStudents.length})
                                        </button>
                                        {groups.map(g => (
                                            <button key={g.id} onClick={() => setSelectedGroupFilter(g.id)}
                                                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${selectedGroupFilter === g.id ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                                data-testid={`students-group-filter-${g.id}`}>
                                                {g.name} ({(g.student_ids || []).filter(sid => enrolledStudents.some(s => s.id === sid)).length})
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {(() => {
                                const studentsTabFiltered = sortBySurname(selectedGroupFilter === 'all'
                                    ? enrolledStudents
                                    : enrolledStudents.filter(s => groups.find(g => g.id === selectedGroupFilter)?.student_ids.includes(s.id)));
                                return studentsTabFiltered.length > 0 ? (
                                <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-50">
                                    {studentsTabFiltered.map(s => (
                                        <div key={s.id} data-testid={`enrolled-${s.id}`}>
                                            <div className="flex items-center justify-between px-5 py-3 hover:bg-gray-50/80 transition-colors cursor-pointer"
                                                onClick={() => setExpandedStudentId(prev => prev === s.id ? null : s.id)}>
                                                <div className="flex items-center gap-3 flex-1">
                                                    <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${expandedStudentId === s.id ? 'bg-ica-gold/20' : 'bg-ica-navy/10'}`}>
                                                        <GraduationCap className={`w-4 h-4 ${expandedStudentId === s.id ? 'text-ica-gold-dark' : 'text-ica-navy'}`} />
                                                    </div>
                                                    <div><p className="text-sm font-medium text-gray-900">{s.name}</p><p className="text-xs text-gray-400">{s.student_id}</p></div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {groups.filter(g => (g.student_ids || []).includes(s.id)).map(g => (
                                                        <span key={g.id} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{g.name}</span>
                                                    ))}
                                                    {getLockForStudent(s.id) && (
                                                        <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${
                                                            getLockForStudent(s.id).locked_by === currentUser?.id
                                                            ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'
                                                        }`} data-testid={`lock-badge-${s.id}`}>
                                                            <Lock className="w-3 h-3" />
                                                            {getLockForStudent(s.id).locked_by === currentUser?.id
                                                                ? 'You' : getLockForStudent(s.id).locked_by_name}
                                                        </span>
                                                    )}
                                                    <button onClick={(e) => { e.stopPropagation(); goToStudentTab(s.id, 'attendance'); }}
                                                        className="p-1.5 rounded-lg text-slate-500 hover:text-ica-navy hover:bg-gray-100 dark:text-slate-400 dark:hover:text-ica-gold dark:hover:bg-slate-700 active:scale-90 transition-all"
                                                        title="View Attendance" data-testid={`goto-attendance-${s.id}`}>
                                                        <ClipboardCheck className="w-4 h-4" />
                                                    </button>
                                                    <button onClick={(e) => { e.stopPropagation(); goToStudentTab(s.id, 'assessment'); }}
                                                        className="p-1.5 rounded-lg text-slate-500 hover:text-ica-navy hover:bg-gray-100 dark:text-slate-400 dark:hover:text-ica-gold dark:hover:bg-slate-700 active:scale-90 transition-all"
                                                        title="View Assessment" data-testid={`goto-assessment-${s.id}`}>
                                                        <Award className="w-4 h-4" />
                                                    </button>
                                                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleUnenroll(s.id); }} className="text-red-400 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 h-8 w-8 p-0" data-testid={`unenroll-${s.id}`} title="Remove Student">
                                                        <UserMinus className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                            {expandedStudentId === s.id && (
                                                <div className="px-5 pb-3 pt-0 flex gap-2 ml-12 animate-in slide-in-from-top-1 duration-150">
                                                    <button onClick={() => goToStudentTab(s.id, 'attendance')}
                                                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 active:scale-95 transition-all"
                                                        data-testid={`expand-attendance-${s.id}`}>
                                                        <ClipboardCheck className="w-4 h-4" /> View Attendance
                                                    </button>
                                                    <button onClick={() => goToStudentTab(s.id, 'assessment')}
                                                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 active:scale-95 transition-all"
                                                        data-testid={`expand-assessment-${s.id}`}>
                                                        <Award className="w-4 h-4" /> View Assessment
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                                    <GraduationCap className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                    <p className="text-gray-500 font-medium">No students enrolled</p>
                                    <p className="text-sm text-gray-400 mt-1">Click "Add Students" to enrol students</p>
                                </div>
                            );
                            })()}
                        </div>
                    </div>
                )}

                {/* Attendance Tab */}
                {activeTab === 'attendance' && (
                    <AttendancePanel
                        students={enrolledStudents}
                        groups={groups}
                        groupFilter={selectedGroupFilter}
                        onGroupFilter={setSelectedGroupFilter}
                        search={attSearch}
                        onSearch={setAttSearch}
                        predefinedComments={predefinedComments}
                        isPresent={(sid) => panelAttendance[sid]}
                        onSetPresent={(sid, val) => setPanelAttendance(p => ({ ...p, [sid]: val }))}
                        predefinedFor={(sid) => attPredefined[sid] || []}
                        onTogglePredefined={(sid, label) => togglePredefined(sid, label)}
                        commentFor={(sid) => attComments[sid] || ''}
                        onSetComment={(sid, val) => setAttComments(p => ({ ...p, [sid]: val }))}
                        onSave={saveAttendance}
                        saving={attSaving}
                    />
                )}

                {/* Assessment Grading Tab */}
                {activeTab === 'assessment' && (
                    <div className="space-y-4" data-testid="assessment-grading-tab">
                        {enrolledStudents.length === 0 ? (
                            <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                                <Award className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                <p className="text-gray-500">No students enrolled yet</p>
                            </div>
                        ) : !formData ? (
                            <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                <p className="text-gray-500">No assessment form linked</p>
                                <p className="text-sm text-gray-400 mt-1">Edit this assessment and select a published form</p>
                            </div>
                        ) : (
                            <>
                                <GradingStudentSelector
                                    students={enrolledStudents}
                                    groups={groups}
                                    groupFilter={selectedGroupFilter}
                                    onGroupFilter={(gf) => { setSelectedGroupFilter(gf); setCurrentGradeIdx(0); }}
                                    currentStudentId={currentGradeStudent?.id}
                                    onSelectStudent={async (sid) => { if (isDirty && currentGradeStudent) await autoSaveCurrentGrade(); if (enrolledStudents[currentGradeIdx]) await releaseLock(enrolledStudents[currentGradeIdx].id); const gi = enrolledStudents.findIndex(st => st.id === sid); setCurrentGradeIdx(gi); loadGrade(sid, panelGrades); await acquireLock(sid); }}
                                    gradedStudentIds={Object.keys(panelGrades)}
                                    lockedStudentIds={enrolledStudents.filter(s => isLockedByOther(s.id)).map(s => s.id)}
                                    rightSlot={
                                        <div className="flex items-center gap-3">
                                            {currentStudentLock && (
                                                <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${currentStudentLockedByOther ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'}`} data-testid="current-lock-badge">
                                                    <Lock className="w-3 h-3" />
                                                    {currentStudentLockedByOther ? `Assessed by ${currentStudentLock.locked_by_name}` : 'Locked by you'}
                                                </span>
                                            )}
                                            <Button variant="outline" size="sm" onClick={() => { setViewFormStudent(currentGradeStudent); setViewFormOpen(true); }} data-testid="view-student-form-btn">
                                                <Eye className="w-4 h-4 mr-1" /> View Form
                                            </Button>
                                            <div className="text-right">
                                                <p className="text-2xl font-bold text-ica-gold">{totalScore}<span className="text-gray-400 text-lg"> / {maxScore}</span></p>
                                            </div>
                                        </div>
                                    }
                                    statusSlot={
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                {isDirty && <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" title="Unsaved changes" />}
                                                <span className="text-xs text-gray-400">{isDirty ? 'Unsaved changes — auto-saves on navigate' : 'All changes saved'}</span>
                                            </div>
                                            <Button onClick={saveGrade} disabled={gradeSaving || currentStudentLockedByOther} className="bg-ica-gold hover:bg-[#a8803a]" data-testid="save-grade-btn-top">
                                                {gradeSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Grade</>}
                                            </Button>
                                        </div>
                                    }
                                />

                                {/* Locked by other warning */}
                                {currentStudentLockedByOther && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3" data-testid="lock-warning">
                                        <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
                                        <div>
                                            <p className="text-sm font-medium text-amber-800">This student is currently being assessed by {currentStudentLock?.locked_by_name}</p>
                                            <p className="text-xs text-amber-600 mt-0.5">You can view but not edit grades. Navigate to another student or wait for the lock to be released.</p>
                                        </div>
                                    </div>
                                )}

                                {/* Grading form - table layout matching physical assessment sheet */}
                                <div className={`bg-white rounded-lg border border-gray-200 overflow-hidden ${currentStudentLockedByOther ? 'opacity-60 pointer-events-none' : ''}`}>
                                    {formData.sections?.map(section => {
                                        const sComments = getCommentsForSection(section.id);
                                        const criteriaCount = section.criteria?.length || 1;
                                        return (
                                            <div key={section.id}>
                                                {/* Section header - full width dark bar */}
                                                <div className="px-4 py-2.5 bg-[#1e3a5f] text-white flex items-center justify-between">
                                                    <h4 className="text-xs font-bold uppercase tracking-wide">{section.title}</h4>
                                                    <div className="flex items-center gap-4 text-xs text-white/60">
                                                        <span>Evaluation</span>
                                                        <span>Comments</span>
                                                    </div>
                                                </div>
                                                {/* Table-like layout: criteria+scores left, merged comments right */}
                                                <div className="flex border-b border-gray-200">
                                                    {/* Left: Task + Evaluation columns */}
                                                    <div className="flex-1 min-w-0 border-r border-gray-200">
                                                        {section.criteria?.map(c => (
                                                            <div key={c.id} className="border-b border-gray-100 last:border-b-0 px-4 py-3" data-testid={`grade-criterion-${c.id}`}>
                                                                <div className="flex items-center gap-3">
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="text-sm text-gray-900 font-medium">{c.label}</p>
                                                                        {c.guidance && <p className="text-xs text-gray-400 mt-0.5">{c.guidance}</p>}
                                                                    </div>
                                                                    <span className="text-xs font-bold text-ica-gold flex-shrink-0 w-8 text-right">{scores[c.id] || '-'}</span>
                                                                    <div className="flex gap-1 flex-shrink-0">
                                                                        {(c.scale || [3,4,5,6,7,8]).map(score => (
                                                                            <button key={score} onClick={() => { setScores(p => { const n = { ...p }; if (n[c.id] === score) { delete n[c.id]; } else { n[c.id] = score; } return n; }); setIsDirty(true); }}
                                                                                className={`w-8 h-8 rounded font-bold text-xs transition-all border ${scores[c.id] === score ? getScoreColor(score, true) + ' shadow-md scale-105 border-transparent' : getScoreColor(score, false) + ' border-gray-200 hover:scale-105'}`}>
                                                                                {score}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {/* Right: Merged Comments column spanning all rows */}
                                                    <div className="w-[280px] flex-shrink-0 flex flex-col p-3" data-testid={`section-comment-area-${section.id}`}
                                                        style={{ minHeight: `${Math.max(criteriaCount * 56, 120)}px` }}>
                                                        {/* Prefilled comment pills at top */}
                                                        {sComments.length > 0 && (
                                                            <div className="flex gap-1.5 flex-wrap mb-2">
                                                                {sComments.map(pc => {
                                                                    const active = (sectionPills[section.id] || []).includes(pc.label);
                                                                    const colorCls = pc.type === 'positive'
                                                                        ? (active ? 'bg-green-500 text-white' : 'bg-green-50 text-green-700 border border-green-200')
                                                                        : pc.type === 'negative'
                                                                        ? (active ? 'bg-red-500 text-white' : 'bg-red-50 text-red-600 border border-red-200')
                                                                        : (active ? 'bg-ica-gold text-white' : 'bg-gray-100 text-gray-600 border border-gray-200');
                                                                    return (
                                                                        <button key={pc.id} onClick={() => toggleSectionPill(section.id, pc.label)}
                                                                            className={`px-2 py-0.5 rounded text-xs font-medium transition-all ${colorCls}`}
                                                                            data-testid={`section-pill-${pc.id}`}>
                                                                            {pc.label}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                        {/* Free text textarea fills remaining space */}
                                                        <Textarea
                                                            value={sectionFreeText[section.id] || ''}
                                                            onChange={e => { setSectionFreeText(p => ({ ...p, [section.id]: e.target.value })); setIsDirty(true); }}
                                                            placeholder={`Comments for ${section.title}...`}
                                                            className="flex-1 text-xs resize-none min-h-[80px]"
                                                            data-testid={`section-freetext-${section.id}`}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div className="p-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Overall Feedback</label>
                                        <Textarea value={feedback} onChange={e => { setFeedback(e.target.value); setIsDirty(true); }} placeholder="Additional comments..." rows={3} className="resize-none text-sm" data-testid="grade-feedback" />
                                    </div>
                                    <Button onClick={saveGrade} disabled={gradeSaving || currentStudentLockedByOther} className="w-full bg-ica-gold hover:bg-[#a8803a]" data-testid="save-grade-btn">
                                        {gradeSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Assessment</>}
                                    </Button>
                                </div>

                                {/* Signatures Section */}
                                <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Signatures</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {['assessor', 'student'].map(role => {
                                            const sig = signatures[role];
                                            return (
                                                <div key={role} className="border border-gray-100 rounded-lg p-4" data-testid={`sig-${role}`}>
                                                    <div className="flex items-center justify-between mb-2">
                                                        <p className="text-sm font-medium text-gray-700 capitalize">{role}</p>
                                                        {sig ? <span className="text-xs text-green-600 font-medium">Signed</span> : <span className="text-xs text-amber-600">Not signed</span>}
                                                    </div>
                                                    {sig ? (
                                                        <div className="space-y-2">
                                                            {sig.signature_type === 'drawn' ? (
                                                                <img src={sig.signature} alt={`${role} signature`} className="h-16 object-contain border rounded" />
                                                            ) : (
                                                                <p className="text-lg italic text-gray-700 font-serif border-b border-gray-200 pb-1">{sig.signature}</p>
                                                            )}
                                                            <p className="text-xs text-gray-400">By {sig.signed_by} — {new Date(sig.signed_at).toLocaleString()}</p>
                                                            <Button variant="outline" size="sm" onClick={() => { setSigModalOpen(role); setSigType('drawn'); setTypedSig(''); }}>
                                                                <PenTool className="w-3.5 h-3.5 mr-1" /> Re-sign
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <Button variant="outline" onClick={() => { setSigModalOpen(role); setSigType('drawn'); setTypedSig(''); }} className="w-full mt-2">
                                                            <PenTool className="w-4 h-4 mr-2" /> Sign as {role}
                                                        </Button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Export */}
                                <div className="flex gap-3">
                                    <Button variant="outline" onClick={exportPDF} className="flex-1" data-testid="export-pdf-btn">
                                        <FileDown className="w-4 h-4 mr-2" /> Export PDF
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Enroll Modal */}
                <Dialog open={enrollModalOpen} onOpenChange={setEnrollModalOpen}>
                    <DialogContent className="max-w-lg" data-testid="enroll-modal">
                        <DialogHeader><DialogTitle>Add Students to {assessment.title}</DialogTitle></DialogHeader>
                        <div className="space-y-4 py-2">
                            <Input value={enrollSearch} onChange={e => setEnrollSearch(e.target.value)} placeholder="Search students..." data-testid="enroll-search" />
                            {availableStudents.length > 0 && (
                                <Button variant="outline" className="w-full" onClick={() => { enrollAll(); setEnrollModalOpen(false); }} data-testid="enroll-all-modal-btn">
                                    <Users className="w-4 h-4 mr-2" /> Add All Year {assessment.year} Students ({availableStudents.length})
                                </Button>
                            )}
                            <div className="max-h-64 overflow-y-auto space-y-1.5">
                                {filteredAvailable.length > 0 ? filteredAvailable.map(s => (
                                    <div key={s.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                        <div><p className="text-sm font-medium">{s.name}</p><p className="text-xs text-gray-500">{s.student_id}</p></div>
                                        <Button size="sm" onClick={() => handleEnroll(s.id)} className="bg-green-600 hover:bg-green-700 h-8" data-testid={`enroll-${s.id}`}>
                                            <UserPlus className="w-3.5 h-3.5 mr-1" /> Add
                                        </Button>
                                    </div>
                                )) : (
                                    <p className="text-center text-gray-400 py-6 text-sm">
                                        {enrollSearch ? 'No matching students' : `All Year ${assessment.year} students are already enrolled`}
                                    </p>
                                )}
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Groups Modal */}
                <Dialog open={groupModalOpen} onOpenChange={(open) => { setGroupModalOpen(open); if (!open) setUploadResult(null); }}>
                    <DialogContent className="max-w-md" data-testid="groups-modal">
                        <DialogHeader><DialogTitle>Manage Groups</DialogTitle></DialogHeader>
                        <div className="space-y-4 py-2">
                            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Upload Groups</p>
                                <p className="text-xs text-gray-400">Upload XLSX/CSV with columns: Group, Student Number / Name / Email</p>
                                <div className="flex gap-2">
                                    <label className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed cursor-pointer transition-all ${uploadingGroups ? 'border-gray-200 bg-gray-100' : 'border-gray-300 hover:border-ica-gold hover:bg-ica-gold/5'}`}>
                                        {uploadingGroups ? <><Loader2 className="w-4 h-4 animate-spin text-gray-400" /><span className="text-sm text-gray-400">Uploading...</span></> : <><Upload className="w-4 h-4 text-gray-500" /><span className="text-sm text-gray-600">Choose File</span></>}
                                        <input type="file" accept=".xlsx,.xls,.csv" onChange={handleGroupUpload} className="hidden" disabled={uploadingGroups} data-testid="group-upload-input" />
                                    </label>
                                    <Button variant="outline" size="sm" onClick={downloadTemplate} className="h-auto" data-testid="download-template-btn">
                                        <Download className="w-4 h-4 mr-1" /> Template
                                    </Button>
                                </div>
                                {uploadResult && (
                                    <div className="text-xs space-y-1 p-2 bg-white rounded-lg border border-gray-100">
                                        <p className="text-green-600 font-medium">{uploadResult.groups_created} created, {uploadResult.groups_updated} updated, {uploadResult.students_enrolled} enrolled</p>
                                        {uploadResult.students_not_found?.length > 0 && (
                                            <div className="flex items-start gap-1 text-amber-600">
                                                <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                                <span>Not found: {uploadResult.students_not_found.join(', ')}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <Input value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder="New group name..." className="flex-1" data-testid="new-group-input"
                                    onKeyDown={e => { if (e.key === 'Enter') createGroup(); }} />
                                <Button onClick={createGroup} data-testid="create-group-btn">Add</Button>
                            </div>
                            {groups.map(g => (
                                <div key={g.id} className="border border-gray-100 rounded-xl p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <p className="text-sm font-medium text-gray-900">{g.name}</p>
                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300" onClick={() => deleteGroup(g.id)} title="Delete Group">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    </div>
                                    <div className="flex gap-1.5 flex-wrap">
                                        {enrolledStudents.map(s => (
                                            <button key={s.id} onClick={() => toggleStudentInGroup(g.id, s.id)}
                                                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${g.student_ids.includes(s.id) ? 'bg-ica-gold text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                                                {s.name.split(' ')[0]}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                            {groups.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No groups yet</p>}
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Signature Modal */}
                <Dialog open={!!sigModalOpen} onOpenChange={open => { if (!open) setSigModalOpen(null); }}>
                    <DialogContent className="max-w-md" data-testid="signature-modal">
                        <DialogHeader><DialogTitle className="capitalize">Sign as {sigModalOpen}</DialogTitle></DialogHeader>
                        <div className="py-4 space-y-4">
                            <div className="flex gap-2">
                                <button onClick={() => setSigType('drawn')}
                                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${sigType === 'drawn' ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600'}`}>
                                    <PenTool className="w-4 h-4 inline mr-1" /> Draw
                                </button>
                                <button onClick={() => setSigType('typed')}
                                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${sigType === 'typed' ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600'}`}>
                                    Type
                                </button>
                            </div>

                            {sigType === 'drawn' ? (
                                <div>
                                    <div className="border-2 border-gray-200 rounded-lg overflow-hidden bg-white">
                                        <SignatureCanvas ref={sigCanvasRef} canvasProps={{ width: 400, height: 150, className: 'w-full' }} />
                                    </div>
                                    <Button variant="ghost" size="sm" className="mt-2 text-gray-400" onClick={() => sigCanvasRef.current?.clear()}>Clear</Button>
                                </div>
                            ) : (
                                <div>
                                    <Input value={typedSig} onChange={e => setTypedSig(e.target.value)} placeholder="Type your full name..." className="text-lg" data-testid="typed-sig-input" />
                                    {typedSig && <p className="mt-3 text-2xl italic font-serif text-gray-700 border-b border-gray-200 pb-2 px-2">{typedSig}</p>}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2 justify-end">
                            <Button variant="outline" onClick={() => setSigModalOpen(null)}>Cancel</Button>
                            <Button onClick={() => saveSignature(sigModalOpen)} className="bg-ica-gold hover:bg-[#a8803a]" data-testid="confirm-signature-btn">
                                <Check className="w-4 h-4 mr-1" /> Save Signature
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* View Student Form Modal - Matches exact template layout */}
                <Dialog open={viewFormOpen} onOpenChange={setViewFormOpen}>
                    <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="view-form-modal">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <FileText className="w-5 h-5 text-ica-navy" />
                                Assessment Form — {viewFormStudent?.name}
                            </DialogTitle>
                        </DialogHeader>
                        {viewFormStudent && formData && (() => {
                            const studentGrade = panelGrades[viewFormStudent.id];
                            const studentScores = {};
                            studentGrade?.scores?.forEach(s => { studentScores[s.criterion_id] = s.score; });
                            const studentGroup = groups.find(g => g._source === 'global' && (g.student_ids || []).includes(viewFormStudent.id));
                            return (
                                <StudentFormView
                                    formData={formData}
                                    studentName={viewFormStudent.name}
                                    studentId={viewFormStudent.student_id}
                                    assessorName={currentUser?.username || ''}
                                    lecturerName={currentUser?.username || ''}
                                    date={assessment?.date || ''}
                                    yearOfStudy={assessment?.year ? `Year ${assessment.year}` : ''}
                                    groupName={studentGroup?.name || ''}
                                    scores={studentScores}
                                    sectionComments={studentGrade?.section_comments || {}}
                                    feedback={studentGrade?.feedback || ''}
                                    signatures={signatures}
                                    assessmentTitle={assessment?.title || ''}
                                    showExport={true}
                                />
                            );
                        })()}
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
};

export default AssessmentDetailPage;
